/**
 * useNextRefCode — пропонує наступний вільний код для ref-сутностей.
 *
 * Запитує існуючі коди через переданий queryFn, шукає максимальний номер
 * з-під префіксу і повертає <PREFIX>-NN (де NN = max+1, padded до 2-3 цифр).
 *
 * Використання:
 *   const { suggested, isLoading } = useNextRefCode({
 *     queryKey: ['retail-price-groups', orgId],
 *     queryFn: () => retailPriceGroups.list({ organization_id: orgId, limit: 500 }),
 *     extractCodes: (data) => data.items.map((i) => i.code),
 *     prefix: 'PG',
 *     enabled: !!orgId && isNew,
 *   });
 */
import { useMemo } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';

interface Args<T> {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  extractCodes: (data: T) => string[];
  prefix: string;
  /** Скільки цифр у номері (default 2). Для 99→3-digit auto-bump. */
  width?: number;
  enabled?: boolean;
}

export function useNextRefCode<T>({
  queryKey,
  queryFn,
  extractCodes,
  prefix,
  width = 2,
  enabled = true,
}: Args<T>) {
  const q = useQuery({ queryKey, queryFn, enabled });

  const suggested = useMemo(() => {
    if (!q.data) return '';
    const codes = extractCodes(q.data);
    // Шукаємо все, що match-ить <prefix>-(\d+)
    const re = new RegExp(`^${escapeRegex(prefix)}-(\\d+)$`, 'i');
    let max = 0;
    for (const c of codes) {
      const m = re.exec(c);
      if (m) {
        const n = parseInt(m[1]!, 10);
        if (!Number.isNaN(n) && n > max) max = n;
      }
    }
    const next = max + 1;
    const effectiveWidth = Math.max(width, String(next).length);
    return `${prefix}-${String(next).padStart(effectiveWidth, '0')}`;
  }, [q.data, prefix, width, extractCodes]);

  return {
    suggested,
    isLoading: q.isLoading,
  };
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
